import React, { useState } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import {
    CheckCircle,
    Users,
    ChevronDown,
    ChevronUp,
    Undo2,
    Mail,
} from "lucide-react";
import BlueprintPickerDialog from "./BlueprintPickerDialog";

const TYPE_LABELS = {
    automation: "Automation",
    full_stack_app: "Full-Stack App",
    integration: "Integration",
    dashboard: "Dashboard",
    other: "Other",
};

export default function RequestCard({ request, onUpdate }) {
    const [expanded, setExpanded] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const { call: updateRequest, isLoading } = useBluelagoon({
        method: "updateBlueprintRequest",
    });

    const handleToggleStatus = async () => {
        if (request.status === "pending") {
            setPickerOpen(true);
            return;
        }
        await updateRequest({ id: request.id, status: "pending" });
        onUpdate();
    };

    const handleBlueprintSelect = async (blueprintId) => {
        setPickerOpen(false);
        await updateRequest({
            id: request.id,
            status: "done",
            blueprintId,
        });
        onUpdate();
    };

    return (
        <Card className={request.status === "done" ? "opacity-60" : ""}>
            <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-base">
                                {request.title}
                            </h3>
                            {request.type && (
                                <Badge variant="outline" className="text-xs">
                                    {TYPE_LABELS[request.type] || request.type}
                                </Badge>
                            )}
                            <Badge variant="secondary" className="shrink-0">
                                <Users className="h-3 w-3 mr-1" />
                                {request.count}{" "}
                                {request.count === 1 ? "user" : "users"}
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {request.description}
                        </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? (
                                <ChevronUp className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                        </Button>
                        {request.status === "done" && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setPickerOpen(true)}
                                disabled={isLoading}
                            >
                                <Mail className="h-4 w-4 mr-1" />
                                Send Emails
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant={
                                request.status === "pending"
                                    ? "default"
                                    : "outline"
                            }
                            onClick={handleToggleStatus}
                            disabled={isLoading}
                        >
                            {request.status === "pending" ? (
                                <>
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Done
                                </>
                            ) : (
                                <>
                                    <Undo2 className="h-4 w-4 mr-1" />
                                    Reopen
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {expanded && <ExpandedDetails request={request} />}
            </CardContent>
            <BlueprintPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={handleBlueprintSelect}
            />
        </Card>
    );
}

function ExpandedDetails({ request }) {
    return (
        <div className="mt-3 pt-3 border-t space-y-3">
            <TagSection label="Features" items={request.features} />
            <TagSection label="Integrations" items={request.integrations} />
            <TagSection label="Filters" items={request.filters} />

            {request.sampleGoals?.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                        Sample user goals:
                    </p>
                    <ul className="space-y-1">
                        {request.sampleGoals.map((goal, i) => (
                            <li
                                key={i}
                                className="text-sm text-muted-foreground bg-muted rounded px-2 py-1"
                            >
                                &ldquo;{goal}&rdquo;
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function TagSection({ label, items }) {
    if (!items?.length) return null;
    return (
        <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">
                {label}:
            </p>
            <div className="flex flex-wrap gap-1">
                {items.map((item, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                        {item}
                    </Badge>
                ))}
            </div>
        </div>
    );
}
